import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BarChart2, Users, Download, Phone, Mail, Send, StickyNote } from '../components/CustomIcons';

const STATUS_OPTS = [
  { value: 'new', label: 'New', color: '#F97316' },
  { value: 'contacted', label: 'Contacted', color: '#60A5FA' },
  { value: 'in_talks', label: 'In talks', color: '#FBBF24' },
  { value: 'closed', label: 'Closed', color: '#4ADE80' },
];

const CTA_LABELS = {
  'nav-schedule': 'Nav · Schedule a Call',
  'hero-platform': 'Hero · View Live Platform',
  'hero-brief': 'Hero · Download Brief',
  'close-platform': 'Close · See Platform Live',
  'close-schedule': 'Close · Schedule Conversation',
  'ask-invest': 'Ask · Download Brief',
  'ask-grant': 'Ask · Grant Partnership',
  'devices-demo': 'Devices · See It In Action',
  'footer-schedule': 'Footer · Schedule a Call',
  'footer-brief': 'Footer · Investment Brief',
  'pilot-submit': 'Pilot Form · Submitted',
  'brief-submit': 'Brief Form · Submitted',
  'faq-schedule': 'FAQ · Schedule a Call',
  'faq-0': 'FAQ · The raise & use of funds',
  'faq-1': 'FAQ · Investor returns & nonprofit',
  'faq-2': 'FAQ · Revenue model',
  'faq-3': 'FAQ · Why now',
  'faq-4': 'FAQ · vs. incumbents',
  'faq-5': 'FAQ · Traction today',
};

const fmtMin = (ms) => {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px', padding: '20px' };
const h2 = { fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px' };
const muted = { fontSize: '12px', color: 'var(--text-muted)' };

export default function MarketingInsights() {
  const { user, authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [digestStatus, setDigestStatus] = useState('idle'); // idle | sending | sent | failed
  const [notesOpen, setNotesOpen] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  useEffect(() => {
    authFetch('/marketing/summary')
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.status === 403 ? 'Founder access only.' : 'Failed to load insights.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLeadStatus = (id, status) => {
    setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? { ...l, status } : l)) }));
    authFetch(`/leads/${id}/status`, { method: 'PATCH', data: { status } }).catch(() => {
      authFetch('/marketing/summary').then((res) => setData(res.data)).catch(() => {});
    });
  };

  const toggleNotes = (l) => {
    if (notesOpen === l.id) { setNotesOpen(null); return; }
    setNotesOpen(l.id);
    setNotesDraft(l.notes || '');
  };

  const saveNotes = async (id) => {
    setNotesSaving(true);
    try {
      await authFetch(`/leads/${id}/notes`, { method: 'PATCH', data: { notes: notesDraft } });
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? { ...l, notes: notesDraft } : l)) }));
      setNotesOpen(null);
    } catch (_e) { /* keep editor open on failure */ }
    setNotesSaving(false);
  };

  const sendDigest = async () => {
    setDigestStatus('sending');
    try {
      const res = await authFetch('/marketing/digest/send', { method: 'POST' });
      setDigestStatus(res.data.email_sent ? 'sent' : 'failed');
    } catch (_e) {
      setDigestStatus('failed');
    }
    setTimeout(() => setDigestStatus('idle'), 4000);
  };

  if (!(user?.profile || {}).is_super_admin) {
    return (
      <div style={{ padding: '48px 24px', display: 'flex', justifyContent: 'center' }} data-testid="marketing-insights-denied">
        <div style={{ ...card, maxWidth: '420px', textAlign: 'center' }}>
          <BarChart2 size={22} color="#F97316" style={{ margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '6px' }}>Founder access only</div>
          <p style={{ ...muted, margin: '0 0 16px', lineHeight: 1.6 }}>Marketing Insights is restricted to the founder account. Exit demo mode or sign in as a founder to view it.</p>
          <Link to="/dashboard" style={{ color: '#F97316', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }} data-testid="mi-denied-back">← Back to dashboard</Link>
        </div>
      </div>
    );
  }
  if (error) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>{error}</div>;
  if (!data) return <div style={{ padding: 32, color: 'var(--text-muted)' }} data-testid="marketing-insights-loading">Loading insights…</div>;

  const { totals, sections, ctas, leads } = data;
  const maxDwell = Math.max(1, ...sections.map((s) => s.total_ms));
  const maxClicks = Math.max(1, ...ctas.map((c) => c.clicks));

  const stats = [
    { icon: Users, label: 'Homepage sessions', value: totals.sessions },
    { icon: Phone, label: 'Call requests', value: totals.pilot_leads },
    { icon: Download, label: 'Brief downloads', value: totals.brief_downloads },
    { icon: Mail, label: 'Auto-email', value: totals.email_configured ? 'Active' : 'Not configured' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }} data-testid="marketing-insights-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--text-primary)', margin: '0 0 4px' }}>Marketing Insights</h1>
          <p style={{ ...muted, margin: '0 0 24px' }}>
            What visitors do on the public homepage — leads, section attention, and CTA performance.
            {totals.alert_email && <> Alerts &amp; digest → <b style={{ color: 'var(--text-primary)' }}>{totals.alert_email}</b> (Mondays 9:00 AM CT).</>}
          </p>
        </div>
        <button
          type="button"
          onClick={sendDigest}
          disabled={digestStatus === 'sending'}
          data-testid="mi-send-digest-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#F97316', color: '#09152A',
            border: 'none', borderRadius: '8px', fontWeight: 800, fontSize: '13px', padding: '10px 18px',
            cursor: 'pointer', opacity: digestStatus === 'sending' ? 0.6 : 1,
          }}
        >
          <Send size={14} />
          {digestStatus === 'sending' ? 'Sending…' : digestStatus === 'sent' ? 'Digest sent ✓' : digestStatus === 'failed' ? 'Send failed — check email config' : "Email me this week's digest"}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '20px' }}>
        {stats.map((s) => (
          <div key={s.label} style={card} data-testid={`mi-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <s.icon size={16} color="#F97316" />
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '26px', color: 'var(--text-primary)', margin: '8px 0 2px' }}>{s.value}</div>
            <div style={muted}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px', marginBottom: '20px' }}>
        <div style={card} data-testid="mi-sections-card">
          <h2 style={h2}><BarChart2 size={15} color="#F97316" /> Section attention (total dwell)</h2>
          {sections.length === 0 && <div style={muted}>No visits recorded yet.</div>}
          {sections.map((s) => (
            <div key={s.name} style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 600 }}>{s.name}</span>
                <span style={{ ...muted, fontFamily: 'JetBrains Mono, monospace' }}>{fmtMin(s.total_ms)} · {s.sessions} session{s.sessions !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(s.total_ms / maxDwell) * 100}%`, background: 'linear-gradient(90deg,#F97316,#FB923C)', borderRadius: '3px' }} />
              </div>
            </div>
          ))}
        </div>
        <div style={card} data-testid="mi-ctas-card">
          <h2 style={h2}><BarChart2 size={15} color="#F97316" /> CTA clicks</h2>
          {ctas.length === 0 && <div style={muted}>No clicks recorded yet.</div>}
          {ctas.map((c) => (
            <div key={c.name} style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 600 }}>{CTA_LABELS[c.name] || c.name}</span>
                <span style={{ ...muted, fontFamily: 'JetBrains Mono, monospace' }}>{c.clicks} click{c.clicks !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(c.clicks / maxClicks) * 100}%`, background: 'linear-gradient(90deg,#F97316,#FB923C)', borderRadius: '3px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={card} data-testid="mi-leads-card">
        <h2 style={h2}><Users size={15} color="#F97316" /> Leads ({leads.length})</h2>
        {leads.length === 0 && <div style={muted}>No leads yet — they'll appear as soon as someone submits the call form or downloads the brief.</div>}
        {leads.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                  {['Type', 'Status', 'Name', 'Email', 'Organization', 'Interest', 'Emailed', 'Date', 'Notes'].map((h) => (
                    <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-default)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <React.Fragment key={l.id}>
                    <tr data-testid={`mi-lead-row-${l.id}`} style={{ color: 'var(--text-primary)' }}>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                        background: l.type === 'pilot_call' ? 'rgba(249,115,22,0.12)' : 'rgba(59,130,246,0.12)',
                        color: l.type === 'pilot_call' ? '#F97316' : '#60A5FA',
                      }}>{l.type === 'pilot_call' ? 'CALL' : 'BRIEF'}</span>
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <select
                        value={l.status || 'new'}
                        onChange={(e) => setLeadStatus(l.id, e.target.value)}
                        data-testid={`mi-lead-status-${l.id}`}
                        style={{
                          background: 'var(--bg-elevated)', borderRadius: '6px', padding: '4px 8px',
                          fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                          color: (STATUS_OPTS.find((s) => s.value === (l.status || 'new')) || STATUS_OPTS[0]).color,
                          border: `1px solid ${(STATUS_OPTS.find((s) => s.value === (l.status || 'new')) || STATUS_OPTS[0]).color}44`,
                        }}
                      >
                        {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 600 }}>{l.name || '—'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>{l.email}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>{l.organization || '—'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>{l.interest || '—'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>{l.type === 'brief_download' ? (l.email_sent ? '✓' : '—') : ''}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{new Date(l.created_at).toLocaleString()}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <button
                        type="button"
                        onClick={() => toggleNotes(l)}
                        data-testid={`mi-lead-notes-btn-${l.id}`}
                        title={l.notes ? 'Edit notes' : 'Add notes'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'none',
                          border: `1px solid ${l.notes ? 'rgba(249,115,22,0.5)' : 'var(--border-default)'}`,
                          color: l.notes ? '#F97316' : 'var(--text-muted)',
                          borderRadius: '6px', padding: '4px 8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        <StickyNote size={12} />{l.notes ? 'Notes' : 'Add'}
                      </button>
                    </td>
                    </tr>
                    {notesOpen === l.id && (
                      <tr data-testid={`mi-lead-notes-row-${l.id}`}>
                        <td colSpan={9} style={{ padding: '10px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                          <textarea
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            placeholder="Who you spoke to, what they said, what's next…"
                            data-testid={`mi-lead-notes-input-${l.id}`}
                            style={{
                              width: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                              borderRadius: '8px', padding: '10px 12px', color: 'var(--text-primary)', fontSize: '13px',
                              fontFamily: 'Lato, sans-serif', resize: 'vertical',
                            }}
                          />
                          <div style={{ display: 'flex', gap: '10px', marginTop: '8px', alignItems: 'center' }}>
                            <button
                              type="button"
                              onClick={() => saveNotes(l.id)}
                              disabled={notesSaving}
                              data-testid={`mi-lead-notes-save-${l.id}`}
                              style={{ background: '#F97316', color: '#09152A', border: 'none', borderRadius: '6px', fontWeight: 800, fontSize: '12px', padding: '7px 16px', cursor: 'pointer', opacity: notesSaving ? 0.6 : 1 }}
                            >{notesSaving ? 'Saving…' : 'Save notes'}</button>
                            <button type="button" onClick={() => setNotesOpen(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                            <span style={{ ...muted, marginLeft: 'auto' }}>{notesDraft.length}/2000</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
